// HomePage.jsx (tout dans le même fichier, i18n intégré)
import React from "react";
import Navbar from "./Navbar";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  BoltIcon,
  SparklesIcon,
  MapPinIcon,
  CalendarDaysIcon,
  MegaphoneIcon,
  PhotoIcon,
  ShoppingBagIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import Button from "../components/controls/Button";
import Footer from "./Footer";
import { useTranslation } from "react-i18next";

/* =========================================================
   Utilitaires d’animation (subtiles & performantes)
   ========================================================= */
function useRevealObserver() {
  React.useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            // Une fois révélé, on n’observe plus (perf)
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// Composant helper pour appliquer la classe reveal + délai
function Reveal({ as: Tag = "div", delay = 0, className = "", children }) {
  return (
    <Tag
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

export default function HomePage() {
  // Active l’observer une seule fois sur la page
  useRevealObserver();

  return (
    <>
      <Navbar />
      <HeroSection />
      <MissionSection />
      <WhyAnanasSection />
      <ServicesSection />
      <Footer></Footer>

      {/* Styles d’animation (légers et sobres) */}
      <style jsx global>{`
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .reveal {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 700ms ease, transform 700ms ease;
          will-change: opacity, transform;
        }
        .reveal.visible {
          opacity: 1;
          transform: none;
          animation: fadeUp 700ms ease both;
        }

        /* Légère variation pour les cartes */
        .reveal.card.visible {
          animation: fadeInScale 500ms ease both;
        }
      `}</style>
    </>
  );
}

/* =========================
   HERO
   ========================= */
function HeroSection() {
  const { t } = useTranslation();

  const BG_URL =
    "https://ldgsoggxiujexptwxyar.supabase.co/storage/v1/object/public/makeadream-images/ImageHomepage/2671ae76-f98c-4ce9-944b-7620cf340320.png";

  React.useEffect(() => {
    const handleScroll = () => {
      const arrow = document.getElementById("scrollArrow");
      if (arrow) {
        const scrollPosition = window.scrollY;
        const screenHeight = window.innerHeight;
        arrow.style.opacity = scrollPosition > screenHeight / 2 ? "0" : "1";
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen w-full ">
      {/* Image de fond limitée au hero */}
      <div
        aria-hidden
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(255,250,240,0) 80%, #FFFAF0 100%)",
        }}
      />

      {/* 2) Image AVEC fade propre, AU-DESSUS du dégradé */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1]"
        style={{
          // calque 1: dégradé #FFF4D8 par-dessus
          // calque 2: image
          backgroundImage: `linear-gradient(to bottom, rgba(255, 211, 100, 0) 95%, rgba(253, 196, 53, 0.35) 100%), url('${BG_URL}')`,
          backgroundSize: "cover, cover",
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundPosition: "80% 20%, 80% 30%",

          // fade-out de l’IMAGE vers transparent en bas
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 90%, rgba(0,0,0,0.4) 95%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 90%, rgba(0,0,0,0.4) 95%, transparent 100%)",
        }}
      />

      {/* Contenu du hero */}
      <div className="relative z-10 h-screen overflow-hidden ">
        <div className="mx-auto max-w-none pl-4 pr-4 pt-24 md:pt-28 lg:pl-[230px] lg:pt-[100px]">
          <div className="max-w-[855px]">
            <Reveal delay={0}>
              <h1
                className="font-bold text-[#3A3A3A] text-[42px] leading-[44px] sm:text:[64px] sm:leading-[68px] lg:text-[96px] lg:leading-[96px]"
                style={{ fontFamily: "'Open Sans', sans-serif" }}
              >
                {t("home.hero.title")}
              </h1>
            </Reveal>

            <Reveal delay={120}>
              <p
                className=" mt-4 md:mt-12 text-[#3A3A3A]/85 text-base leading-6 sm:text-lg sm:leading-7 ml-1 max-w-[740px]"
                style={{ fontFamily: "'Open Sans', sans-serif" }}
              >
                {t("home.hero.subtitle.line1")}
                <br />
                {t("home.hero.subtitle.line2")}
              </p>
            </Reveal>

            <Reveal delay={220}>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <Button
                  as="a"
                  size="lg"
                  href="/boutique"
                  fullWidth
                  className="sm:w-auto"
                >
                  {t("home.hero.cta.exploreShop")}
                </Button>

                {/* Lien centré en mobile */}
                <a
                  href="/creer-evenement"
                  className="inline-flex w-full sm:w-auto justify-center sm:justify-start
               items-center text-[#3A3A3A]/80 font-medium"
                >
                  <span className="underline hover:text-[#3A3A3A]">
                    {t("home.hero.cta.createEvent")}
                  </span>
                  <ChevronRightIcon className="ml-1 h-5 w-5" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Flèche scroll */}
        <div
          id="scrollArrow"
          className="absolute bottom-5 left-1/2 transform -translate-x-1/2 z-10 transition-opacity duration-500"
          style={{
            animation: "slow-bounce 2.5s infinite ease-in-out",
          }}
        >
          <svg
            className="w-10 h-10 text-[#3A3A3A] opacity-80 drop-shadow-md"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        <style jsx>{`
          @keyframes slow-bounce {
            0%,
            100% {
              transform: translateY(0) translateX(-50%);
            }
            50% {
              transform: translateY(15px) translateX(-50%);
            }
          }
        `}</style>
      </div>
    </section>
  );
}

/* =========================
   NOTRE MISSION (impact)
   ========================= */
function MissionSection() {
  const { t } = useTranslation();
  return (
    <section
      id="mission"
      className="relative w-full bg-[#FFFAF0] py-16 md:py-24"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* En-tête court */}
        <div className="max-w-3xl">
          <Reveal delay={0}>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#3A3A3A]"
              style={{ fontFamily: "'Open Sans', sans-serif" }}
            >
              {t("home.mission.title")}
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-4 text-[#3A3A3A]/80 text-base sm:text-lg leading-relaxed">
              {t("home.mission.paragraph")}
            </p>
          </Reveal>
        </div>

        {/* Bande d'impact (chiffres) */}
        <div className="mt-10 grid gap-6 sm:grid-cols-3 text-center">
          <Reveal className="card">
            <Stat
              kpi={t("home.mission.kpis.k1.value")}
              label={t("home.mission.kpis.k1.label")}
              note=""
            />
          </Reveal>
          <Reveal className="card" delay={80}>
            <Stat
              kpi={t("home.mission.kpis.k2.value")}
              label={t("home.mission.kpis.k2.label")}
              note=""
            />
          </Reveal>
          <Reveal className="card" delay={160}>
            <Stat
              kpi={t("home.mission.kpis.k3.value")}
              label={t("home.mission.kpis.k3.label")}
              note=""
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Stat({ kpi, label, note }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm">
      <div className="text-4xl font-extrabold text-[#3A3A3A]">{kpi}</div>
      <div className="mt-1 font-medium text-[#3A3A3A]">{label}</div>
      <div className="mt-1 text-sm text-[#3A3A3A]/60">{note}</div>
    </div>
  );
}

/* =========================
   POURQUOI (avantages – timeline)
   ========================= */
function WhyAnanasSection() {
  const { t } = useTranslation();
  return (
    <section
      id="pourquoi"
      className="relative w-full py-20 md:py-28 bg-gradient-to-b from-[#FFFAF0] to-[#FFF4D8]"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-16 lg:grid-cols-[420px,1fr]">
        {/* Panneau gauche (sticky) */}
        <div className=" lg:top-24 self-start">
          <Reveal>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#3A3A3A] leading-tight text-center md:text-left"
              style={{ fontFamily: "'Open Sans', sans-serif" }}
            >
              {t("home.why.title")}
            </h2>
          </Reveal>

          <Reveal delay={120}>
            <p className="mt-6 text-[#3A3A3A]/85 text-base sm:text-lg leading-relaxed">
              {t("home.why.paragraph")}
            </p>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-8 flex flex-wrap items-center gap-4 flex-col md:flex-row">
              <Button as="a" href="/devenir-partenaire">
                {t("home.why.cta.becomePartner")}
              </Button>
              <a
                href="/boutique"
                className="inline-flex items-center text-[#3A3A3A] font-medium underline underline-offset-2 hover:text-[#C85A2C]"
              >
                {t("home.why.cta.exploreShop")}
              </a>
            </div>
          </Reveal>
        </div>

        {/* Timeline droite */}
        <ol className="relative pl-6 space-y-10 before:absolute before:left-3 before:top-0 before:h-full before:w-[2px] before:bg-[#E9D8A6]/70">
          <Reveal as="li" className="relative ml-3 group">
            <WhyStep
              icon={SparklesIcon}
              title={t("home.why.steps.visibility.title")}
              text={t("home.why.steps.visibility.text")}
            />
          </Reveal>

          <Reveal as="li" className="relative ml-3 group" delay={80}>
            <WhyStep
              icon={CalendarDaysIcon}
              title={t("home.why.steps.dreams.title")}
              text={t("home.why.steps.dreams.text")}
            />
          </Reveal>

          <Reveal as="li" className="relative ml-3 group" delay={160}>
            <WhyStep
              icon={MapPinIcon}
              title={t("home.why.steps.quebecMade.title")}
              text={t("home.why.steps.quebecMade.text")}
            />
          </Reveal>

          <Reveal as="li" className="relative ml-3 group" delay={240}>
            <WhyStep
              icon={BoltIcon}
              title={t("home.why.steps.simple.title")}
              text={t("home.why.steps.simple.text")}
            />
          </Reveal>
        </ol>
        <div className="pointer-events-none absolute -bottom-8 left-0 right-0 h-8 bg-gradient-to-t from-transparent to-[#e4f3de]/70" />
      </div>
    </section>
  );
}

function WhyStep({ icon: Icon, title, text, index }) {
  return (
    <div className="relative">
      {/* pastille icône */}
      <div className="absolute -left-[34px] top-1 grid h-10 w-10 place-items-center rounded-full bg-[#FFD76A]  shadow-md ring-2 ring-white group-hover:scale-110 group-hover:bg-[#F3DA77] transition">
        <Icon className="block h-5 w-5 text-[#3A3A3A]" />
      </div>

      {/* contenu */}
      <div className="rounded-2xl border border-black/5 bg-white/95 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition">
        {index && (
          <div className="mb-1 text-xs font-semibold tracking-wider text-[#C85A2C]/70">
            {index}
          </div>
        )}
        <h3
          className="text-lg sm:text-xl font-semibold text-[#3A3A3A] group-hover:text-[#C85A2C] transition"
          style={{ fontFamily: "'Open Sans', sans-serif" }}
        >
          {title}
        </h3>
        <p className="mt-2 text-[#3A3A3A]/80 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

export function ServicesSection() {
  const { t } = useTranslation();
  return (
    <section
      id="services-entreprises"
      className="relative w-full py-20 md:py-28 bg-[#FFFAF0]"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="max-w-3xl mb-12">
          <Reveal>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#3A3A3A]"
              style={{ fontFamily: "'Open Sans', sans-serif" }}
            >
              {t("home.services.title")}
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-4 text-[#3A3A3A]/85 text-base sm:text-lg leading-relaxed">
              {t("home.services.intro")}
            </p>
          </Reveal>
        </div>

        {/* Grille de services */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-2">
          <Reveal className="card">
            <ServiceCard
              icon={MegaphoneIcon}
              title={t("home.services.blocks.visibility.title")}
              points={[
                t("home.services.blocks.visibility.points.0"),
                t("home.services.blocks.visibility.points.1"),
                t("home.services.blocks.visibility.points.2"),
                t("home.services.blocks.visibility.points.3"),
              ]}
            />
          </Reveal>

          <Reveal className="card" delay={80}>
            <ServiceCard
              icon={PhotoIcon}
              title={t("home.services.blocks.content.title")}
              points={[
                t("home.services.blocks.content.points.0"),
                t("home.services.blocks.content.points.1"),
                t("home.services.blocks.content.points.2"),
                t("home.services.blocks.content.points.3"),
                t("home.services.blocks.content.points.4"),
              ]}
            />
          </Reveal>

          <Reveal className="card" delay={160}>
            <ServiceCard
              icon={ShoppingBagIcon}
              title={t("home.services.blocks.shopTickets.title")}
              points={[
                t("home.services.blocks.shopTickets.points.0"),
                t("home.services.blocks.shopTickets.points.1"),
                t("home.services.blocks.shopTickets.points.2"),
              ]}
            />
          </Reveal>

          <Reveal className="card" delay={240}>
            <ServiceCard
              icon={LightBulbIcon}
              title={t("home.services.blocks.strategy.title")}
              points={[
                t("home.services.blocks.strategy.points.0"),
                t("home.services.blocks.strategy.points.1"),
                t("home.services.blocks.strategy.points.2"),
                t("home.services.blocks.strategy.points.3"),
              ]}
            />
          </Reveal>
        </div>

        {/* CTA */}
        <Reveal delay={200}>
          <div className="mt-12 flex flex-col items-center gap-4 justify-center">
            <div>
              <a
                href="/trouver-un-representant"
                className="inline-flex items-center justify-center rounded-full bg-[#FFD76A] px-6 py-3 font-semibold text-[#3A3A3A] shadow-sm hover:bg-[#F3DA77] transition"
              >
                {t("home.services.cta.findRep")}
              </a>
            </div>
            <div>
              <a
                href="/contact"
                className="inline-flex items-center font-medium text-[#3A3A3A] underline underline-offset-2 hover:text-[#C85A2C]"
              >
                {t("home.services.cta.contactUs")}
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* Carte de service réutilisable */
export function ServiceCard({ icon: Icon, title, points = [] }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm hover:shadow-md transition md:min-h-[275px]">
      <div className="flex items-center gap-3 mb-4">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#FFD76A] shadow">
          <Icon className="h-6 w-6 text-[#3A3A3A]" />
        </span>
        <h3
          className="text-lg sm:text-xl font-semibold text-[#3A3A3A]"
          style={{ fontFamily: "'Open Sans', sans-serif" }}
        >
          {title}
        </h3>
      </div>

      <ul className="space-y-2 text-[#3A3A3A]/80">
        {points.map((p, i) => (
          <li key={i} className="flex gap-2 items-start">
            <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-[#FFD76A] shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
